import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Search, ChevronDown, SlidersHorizontal, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TemplateSortOption, TemplateFilterOption } from '../types';
import { SORT_LABELS } from '../constants';

interface TemplateHeaderControlsProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sortBy: TemplateSortOption;
  onSortChange: (value: TemplateSortOption) => void;
  filterType: TemplateFilterOption;
  onFilterChange: (value: TemplateFilterOption) => void;
  onNewTemplateClick: () => void;
}

export const TemplateHeaderControls: React.FC<TemplateHeaderControlsProps> = ({
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
  filterType,
  onFilterChange,
  onNewTemplateClick,
}) => {
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  return (
    <div className="w-full flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
      {/* Search input */}
      <div className="relative flex-grow md:max-w-[360px] lg:max-w-[400px]">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400 stroke-[1.8]" />
        <input
          type="text"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full h-11 pl-10 pr-4 bg-white border border-slate-200 rounded-[10px] text-slate-800 text-sm placeholder-slate-400/90 focus:outline-none focus:ring-1 focus:ring-purple-500/25 focus:border-purple-500/40 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all"
        />
      </div>

      {/* Right tools side */}
      <div className="flex items-center gap-3 self-stretch md:self-auto justify-end">
        {/* Sort By Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              setIsSortDropdownOpen(!isSortDropdownOpen);
              setIsFilterDropdownOpen(false);
            }}
            className="h-11 px-4 border border-slate-200 rounded-[10px] bg-white text-slate-700 text-sm font-medium flex items-center justify-between gap-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] hover:bg-slate-50 transition-colors focus:outline-none"
          >
            <span>Sort by: {SORT_LABELS[sortBy]}</span>
            <ChevronDown
              className="h-4 w-4 text-slate-500 transition-transform duration-200"
              style={{ transform: isSortDropdownOpen ? 'rotate(180deg)' : 'none' }}
            />
          </button>

          {isSortDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsSortDropdownOpen(false)}
              />
              <div className="absolute right-0 mt-1.5 w-48 rounded-[10px] border border-slate-100 bg-white p-1 shadow-lg z-40 focus:outline-none">
                {(['recently_updated', 'oldest', 'name_asc', 'name_desc'] as TemplateSortOption[]).map(
                  (opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        onSortChange(opt);
                        setIsSortDropdownOpen(false);
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-xs font-medium rounded-md transition-colors',
                        sortBy === opt
                          ? 'bg-purple-50 text-purple-700'
                          : 'text-slate-700 hover:bg-slate-50'
                      )}
                    >
                      {SORT_LABELS[opt]}
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </div>

        {/* Filters Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              setIsFilterDropdownOpen(!isFilterDropdownOpen);
              setIsSortDropdownOpen(false);
            }}
            className={cn(
              'h-11 px-4 border rounded-[10px] text-sm font-medium flex items-center gap-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors focus:outline-none',
              filterType !== 'all'
                ? 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100/50'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            )}
          >
            <SlidersHorizontal className="h-4 w-4 text-slate-500" />
            <span>
              Filters
              {filterType !== 'all'
                ? ` (${filterType === 'variables' ? 'Vars' : 'No vars'})`
                : ''}
            </span>
          </button>

          {isFilterDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsFilterDropdownOpen(false)}
              />
              <div className="absolute right-0 mt-1.5 w-48 rounded-[10px] border border-slate-100 bg-white p-1 shadow-lg z-40 focus:outline-none">
                <button
                  onClick={() => {
                    onFilterChange('all');
                    setIsFilterDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs font-medium rounded-md transition-colors',
                    filterType === 'all'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  )}
                >
                  All templates
                </button>
                <button
                  onClick={() => {
                    onFilterChange('variables');
                    setIsFilterDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs font-medium rounded-md transition-colors',
                    filterType === 'variables'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  )}
                >
                  {'Contains variables ({{}})'}
                </button>
                <button
                  onClick={() => {
                    onFilterChange('no_variables');
                    setIsFilterDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs font-medium rounded-md transition-colors',
                    filterType === 'no_variables'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  )}
                >
                  No variables
                </button>
              </div>
            </>
          )}
        </div>

        {/* New Template button */}
        <Button
          onClick={onNewTemplateClick}
          className="h-11 bg-[#A655F8] hover:bg-[#9333EA] text-white font-semibold rounded-[10px] px-4 flex items-center gap-2 transition-all shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
        >
          <Plus className="w-4.5 h-4.5" />
          <span>New Template</span>
        </Button>
      </div>
    </div>
  );
};
